import { useCallback, useState } from 'react';

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_SIZE_STORAGE_KEY = 'pageSize';

interface UsePageSizeOptions {
	defaultPageSize?: number;
	storageKey?: string;
}

interface UsePageSizeResult {
	pageSize: number;
	setPageSize: (nextPageSize: number) => void;
	storePageSize: (nextPageSize: number) => void;
}

const normalizePageSize = (value: unknown, fallback: number): number => {
	if (typeof value !== 'number' || Number.isNaN(value) || value <= 0) {
		return fallback;
	}

	return value;
};

export const usePageSize = ({
	defaultPageSize = DEFAULT_PAGE_SIZE,
	storageKey = DEFAULT_PAGE_SIZE_STORAGE_KEY,
}: UsePageSizeOptions = {}): UsePageSizeResult => {
	const [pageSize, setPageSizeState] = useState<number>(() => {
		if (typeof window === 'undefined') {
			return defaultPageSize;
		}

		try {
			const storedPageSize = localStorage.getItem(storageKey);

			if (!storedPageSize) {
				return defaultPageSize;
			}

			return normalizePageSize(Number(storedPageSize), defaultPageSize);
		} catch (error) {
			console.warn('Failed to load page size from localStorage:', error);
			return defaultPageSize;
		}
	});

	const storePageSize = useCallback((nextPageSize: number) => {
		const normalizedPageSize = normalizePageSize(nextPageSize, defaultPageSize);

		setPageSizeState(normalizedPageSize);

		if (typeof window === 'undefined') {
			return;
		}

		try {
			localStorage.setItem(storageKey, String(normalizedPageSize));
		} catch (error) {
			console.warn('Failed to store page size in localStorage:', error);
		}
	}, [defaultPageSize, storageKey]);

	return {
		pageSize,
		setPageSize: storePageSize,
		storePageSize,
	};
};

export default usePageSize;
